import type { TreeSelectionType } from '#domain/core/filterDefinition/types/treeSelection.type';
import {
  adaptSelectedFilter,
  legacyNodeIdsToTreeSelections,
  serializeLegacyPeriod,
  treeSelectionsToLegacyNodeIds
} from './filterMapping.utils';
import type { BaseFilterMappingType } from './types/filterMapping.type';

export const DB_TO_LEGACY_MAP = {
  value: {
    target: 'root',
    legacyKey: 'isWithTax',
    toLegacy: (dbValue: string) => dbValue === 'TTC',
    toDB: (legacyValue: boolean) => (legacyValue ? 'TTC' : 'HT')
  },
  // `toLegacy` is null on purpose: the period needs the submitted granularity and the refresh state,
  // so its conversion lives in partitionFilterUpdates (filterMapping.utils.ts).
  period: {
    target: 'root',
    legacyKey: 'selectedPeriod',
    toLegacy: null,
    toDB: serializeLegacyPeriod
  },

  taxonomy: {
    target: 'root',
    legacyKey: 'taxonomy',
    toLegacy: null,
    toDB: null
  },
  store_perimeter: {
    target: 'root',
    legacyKey: 'storePerimeter',
    toLegacy: null,
    toDB: null
  },
  product_filter_type: {
    target: 'root',
    legacyKey: 'productFilterType',
    toLegacy: null,
    toDB: null
  },
  is_my_data_included: {
    target: 'root',
    legacyKey: 'isMyDataIncluded',
    toLegacy: null,
    toDB: null
  },
  referential_intervals: {
    target: 'root',
    legacyKey: 'referentialIntervals',
    toLegacy: null,
    toDB: null
  },
  comparison_period: {
    target: 'root',
    legacyKey: 'comparisonPeriod',
    toLegacy: null,
    toDB: null
  },
  /**
   * `history` and `custom_history` are deliberately absent from this map: they both feed the
   * single legacy `comparisonPeriod`, which a 1-to-1 mapping cannot express. Their conversion
   * lives in buildComparisonPeriod / resolveLegacyFieldValue (filterMapping.utils.ts).
   */

  activity_type: {
    target: 'selectedFilters',
    legacyKey: 'activityTypesFilters',
    toDB: (legacyValue: string[]) => legacyValue.map((e) => e.split('=')[1]),
    toLegacy: (dbValue: string[]) => adaptSelectedFilter('activityTypesFilters', 'activityType', dbValue)
  },
  store_type: {
    target: 'selectedFilters',
    legacyKey: 'integratedFranchisedFilters',
    toLegacy: (dbValue: string[]) =>
      adaptSelectedFilter('integratedFranchisedFilters', 'integratedFranchised', dbValue),
    toDB: (legacyValue: string[]) => legacyValue.map((e) => e.split('=')[1])
  },
  granularity: {
    target: 'root',
    legacyKey: 'granularity',
    toLegacy: null,
    toDB: null
  },
  triptych: {
    target: 'selectedFilters',
    legacyKey: 'triptyqueFilters',
    toLegacy: (dbValue: string[]) => adaptSelectedFilter('triptyqueFilters', 'brandType', dbValue),
    toDB: null
  },
  /** Tree filters: typed key paths in, legacy nodeIds out (see TreeFilterSelection). */
  store: {
    target: 'selectedFilters',
    legacyKey: 'storesFilters',
    toLegacy: (dbValue: TreeSelectionType[]) => treeSelectionsToLegacyNodeIds('storesFilters', dbValue),
    toDB: (legacyValue: string[]) => legacyNodeIdsToTreeSelections(legacyValue)
  },
  business_brand: {
    target: 'selectedFilters',
    legacyKey: 'businessBrandFilters',
    toLegacy: (dbValue: TreeSelectionType[]) => treeSelectionsToLegacyNodeIds('businessBrandFilters', dbValue),
    toDB: (legacyValue: string[]) => legacyNodeIdsToTreeSelections(legacyValue)
  },
  store_region: {
    target: 'selectedFilters',
    legacyKey: 'storesRegionFilters',
    toLegacy: (dbValue: TreeSelectionType[]) => treeSelectionsToLegacyNodeIds('storesRegionFilters', dbValue),
    toDB: (legacyValue: string[]) => legacyNodeIdsToTreeSelections(legacyValue)
  },
  nomenclature: {
    target: 'selectedFilters',
    legacyKey: 'nomenclatureFilters',
    toLegacy: (dbValue: TreeSelectionType[]) => treeSelectionsToLegacyNodeIds('nomenclatureFilters', dbValue),
    toDB: (legacyValue: string[]) => legacyNodeIdsToTreeSelections(legacyValue)
  },
  customer_segmentation_conso_style: {
    target: 'selectedFilters',
    legacyKey: 'consostyleMicroFilters',
    toDB: (legacyValue: string[]) => legacyValue.map((e) => e.split('=')[1]),
    toLegacy: (dbValue: string[]) => adaptSelectedFilter('consostyleMicroFilters', 'consostyleMicro', dbValue)
  },
  customer_segmentation_life_stage: {
    target: 'selectedFilters',
    legacyKey: 'lifestageMicroFilters',
    toDB: (legacyValue: string[]) => legacyValue.map((e) => e.split('=')[1]),
    toLegacy: (dbValue: string[]) => adaptSelectedFilter('lifestageMicroFilters', 'lifestageMicro', dbValue)
  },
  brand: {
    target: 'selectedFilters',
    legacyKey: 'brandsFilters',
    toDB: (legacyValue: string[]) => legacyValue.map((e) => e.split('=')[1]),
    toLegacy: (dbValue: string[]) => adaptSelectedFilter('brandsFilters', 'brand', dbValue)
  },
  holding: {
    target: 'selectedFilters',
    legacyKey: 'bemHoldingFilters',
    toDB: (legacyValue: string[]) => legacyValue.map((e) => e.split('=')[1]),
    toLegacy: (dbValue: string[]) => adaptSelectedFilter('bemHoldingFilters', 'bemMainHolding', dbValue)
  },
  supplier: {
    target: 'selectedFilters',
    legacyKey: 'suppliersFilters',
    toLegacy: (dbValue: TreeSelectionType[]) => treeSelectionsToLegacyNodeIds('suppliersFilters', dbValue),
    toDB: (legacyValue: string[]) => legacyNodeIdsToTreeSelections(legacyValue)
  },
  ean: {
    target: 'selectedFilters',
    legacyKey: 'barcodeFilters',
    toDB: (legacyValue: string[]) => legacyValue.map((e) => e.split('=')[1]),
    toLegacy: (dbValue: string[]) => adaptSelectedFilter('barcodeFilters', 'barcode', dbValue)
  },
  item: {
    target: 'selectedFilters',
    legacyKey: 'itemFilters',
    toDB: (legacyValue: string[]) => legacyValue.map((e) => e.split('=')[1]),
    toLegacy: (dbValue: string[]) => adaptSelectedFilter('itemFilters', 'item', dbValue)
  }
} satisfies Record<string, BaseFilterMappingType>;
